import {
  AbstractMesh,
  Color3,
  PBRMaterial,
  SceneLoader,
  Texture,
  TransformNode,
  type AnimationGroup,
  type AssetContainer,
  type Scene,
  type Skeleton,
} from '@babylonjs/core';
import {
  createLogger,
  type SkeletonType,
  type CharacterModel,
  type CharacterTextureVariant,
  type EquipmentPiece,
  type BodyPart,
  type VisualAttachmentSource,
  type NpcModelState,
  type AnimationVocabulary,
} from '@sorrem/shared';
import { loadAssetCatalog } from '../asset-catalog';
import { type AnimationBase, loadAnimationBase } from './character-animation-base';
import { instantiateCharacterMesh, LoadedCharacterMesh } from './character-mesh';
import { arrayBufferToDataUri, loadAnimationBaseBytes, loadCharacterModelBytes, loadCharacterTextureBytes, registerModelContentHashes } from '../model-cache';
import { resolveEquipmentAttachments, type AssetCache, type OutfitBody } from './equipment-mesh-loader';
import { LoadOnceCache } from '../load-once-cache';

const logger = createLogger('engine:character-loader');

/**
 * Client-side asset cache for loaded 3D character components.
 * Each character model's glTF is loaded once into an `AssetContainer`;
 * every requested character instance (including the first) is produced via
 * `instantiateModelsToScene`, which gives it its own transform hierarchy,
 * skeleton, and animation groups (so playing an animation on one instance
 * never affects another sharing the same model) without re-fetching or
 * re-parsing the source file. Materials are deduplicated by tileset+id.
 */
export interface CharacterInstance {
  readonly root: TransformNode;
  readonly meshes: readonly AbstractMesh[];
  readonly skeletons: readonly Skeleton[];
  readonly animationGroups: readonly AnimationGroup[];
  readonly transformNodes: readonly TransformNode[];
}

export class CharacterAssetCache {
  private readonly containers = new LoadOnceCache<string, AssetContainer>((container) => container.dispose());
  private readonly animationVocabs = new LoadOnceCache<string, AnimationVocabulary>();
  private readonly animationBases = new LoadOnceCache<string, AnimationBase>((base) => base.animationGroups.forEach((group) => group.dispose()));

  private async loadContainer(
    scene: Scene,
    characterModelId: string,
  ): Promise<AssetContainer> {
    // Multiple NPCs sharing a model (e.g. two zombies on the same map) can
    // call instantiateCharacter concurrently before either finishes; the
    // cache shares one fetch of the (often multi-MB) file between them, and
    // drops a failed one so the character can be selected again.
    // Routed through fetchModelWithCache (IndexedDB+ETag) rather than a
    // raw SceneLoader network fetch, so a model already warmed by a
    // crucible-gathering-time preload (see model-cache.ts) is genuinely
    // skipped here instead of being re-fetched.
    return this.containers.get(characterModelId, () => loadCharacterModelBytes(characterModelId)
      .then((meshData) => SceneLoader.LoadAssetContainerAsync('', arrayBufferToDataUri(meshData), scene)));
  }

  /** Loads a character model ahead of its first `instantiateCharacter`. */
  async preloadCharacter(scene: Scene, characterModelId: string): Promise<void> {
    await this.loadContainer(scene, characterModelId);
  }

  /**
   * Instantiate an independent copy of a character model: its own transform
   * hierarchy, skeleton, and animation groups, cloned from the cached
   * `AssetContainer` (loaded from the network only once per model).
   */
  async instantiateCharacter(
    scene: Scene,
    characterModelId: string,
    meshFile: string,
  ): Promise<CharacterInstance> {
    const container = await this.loadContainer(scene, characterModelId);
    return instantiateCharacterMesh(container, `${meshFile} (${characterModelId})`);
  }

  /**
   * Get or load animation vocabulary for a skeleton type. Multiple
   * characters sharing a skeleton type can request this concurrently before
   * either finishes - caches the in-flight promise itself (same reasoning
   * as `getContainer()`) so concurrent callers await one fetch instead of
   * each firing an independent request for the same vocabulary file.
   */
  async loadAnimationVocabulary(
    skeletonTypeId: string,
    vocabUrl: string,
  ): Promise<AnimationVocabulary> {
    return this.animationVocabs.get(skeletonTypeId, async () => {
      const response = await fetch(vocabUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch animation vocabulary: ${vocabUrl}`);
      }
      return (await response.json()) as AnimationVocabulary;
    });
  }

  /**
   * Get or load the shared animation base (skeleton + baked animation
   * groups) for a skeleton type - fetched and parsed at most once per
   * skeleton type, not once per entity. Every character sharing this
   * skeleton type (e.g. every "humanoid" NPC on a map) previously re-parsed
   * the entire animation-base glTF for itself even though the IndexedDB
   * byte cache was already warm; parsing a multi-animation glTF into
   * `AnimationGroup`s is real CPU work `fetchWithCache`'s byte-level cache
   * can't avoid on its own. Callers must retarget their own clone of the
   * returned groups (`cloneRetargetedAnimationGroups`) rather than mutating
   * them in place - this base is shared and reused for every subsequent
   * caller, including ones needing a different character's joint mapping.
   */
  async loadAnimationBase(
    skeletonTypeId: string,
    load: () => Promise<AnimationBase>,
  ): Promise<AnimationBase> {
    return this.animationBases.get(skeletonTypeId, load);
  }

  /**
   * Clear all cached assets. Called when unloading a scene/game.
   */
  dispose(): void {
    this.containers.clear();
    this.animationVocabs.clear();
    this.animationBases.clear();
  }
}

/**
 * Main character loader: assembles a complete character from data.
 * Loads the model container once, instantiates a fresh copy per character.
 */
export class CharacterLoader {
  private cache: CharacterAssetCache;
  /** Texture-variant materials by scene, model, variant and source material (see `applyTextureVariant`). */
  private readonly variantMaterials = new LoadOnceCache<string, PBRMaterial>((material) => material.dispose(false, true));
  private assetBaseUrl: string;
  private apiBaseUrl: string;
  private skeletonTypes: SkeletonType[] = [];
  private characters: Map<string, CharacterModel> = new Map();
  private equipment: Map<string, EquipmentPiece> = new Map();
  private bodyParts: Map<string, BodyPart> = new Map();

  constructor(assetBaseUrl: string, apiBaseUrl: string = '/api') {
    this.assetBaseUrl = assetBaseUrl;
    this.apiBaseUrl = apiBaseUrl;
    this.cache = new CharacterAssetCache();
  }

  /**
   * Initialize: load all character/skeleton/equipment/body-part data from API endpoints.
   */
  async initialize(): Promise<void> {
    this.skeletonTypes = await loadAssetCatalog<SkeletonType>(`${this.apiBaseUrl}/skeleton-types`);
    // The catalogs list each model's content hash, so cached copies that match load with no request.
    registerModelContentHashes('animation', this.skeletonTypes.map((type) => [type.id, type.animationBaseFileHash] as const));

    const chars = await loadAssetCatalog<CharacterModel>(`${this.apiBaseUrl}/characters`);
    for (const char of chars) {
      this.characters.set(char.id, char);
    }
    registerModelContentHashes('character', chars.map((char) => [char.id, char.bodyMeshHash] as const));

    // Equipment (optional for now). Server-side this catalog is served as
    // "items" (see assets-router.ts's /assets/items, backed by items.json)
    // - "equipment" was the old name and never had its own route, so this
    // fetch 404'd on every load until now.
    try {
      const eq = await loadAssetCatalog<EquipmentPiece>(`${this.apiBaseUrl}/items`);
      for (const piece of eq) {
        this.equipment.set(piece.id, piece);
      }
      registerModelContentHashes('equipment', eq.map((piece) => [piece.id, piece.modelHash] as const));
    } catch (error) {
      logger.warn('Equipment loading failed (non-critical):', error);
    }

    // Body parts (cosmetic outfits, hair, eyebrows)
    try {
      const parts = await loadAssetCatalog<BodyPart>(`${this.apiBaseUrl}/body-parts`);
      for (const part of parts) {
        this.bodyParts.set(part.id, part);
      }
      registerModelContentHashes('body-part', parts.map((part) => [part.id, part.modelHash] as const));
    } catch (error) {
      logger.warn('Body parts loading failed (non-critical):', error);
    }
  }

  /**
   * Load a complete character: skeleton, body mesh, equipment.
   * Returns a LoadedCharacterMesh that can be positioned/animated.
   */
  async loadCharacter(
    scene: Scene,
    npcData: NpcModelState,
  ): Promise<LoadedCharacterMesh> {
    const charModel = this.characters.get(npcData.characterModelId);
    if (!charModel) {
      throw new Error(`Character model not found: ${npcData.characterModelId}`);
    }

    const skelType = this.skeletonTypes.find(st => st.id === charModel.skeletonTypeId);
    if (!skelType) {
      throw new Error(`Skeleton type not found: ${charModel.skeletonTypeId}`);
    }

    // Get body mesh - this determines which animations we load
    const meshFile = npcData.meshVariantId
      ? charModel.meshVariants?.find(v => v.id === npcData.meshVariantId)?.file ||
        charModel.bodyMesh
      : charModel.bodyMesh;

    const instance = await this.cache.instantiateCharacter(
      scene,
      npcData.characterModelId,
      meshFile,
    );

    if (instance.skeletons.length === 0) {
      throw new Error(
        `loadCharacter: ${meshFile} imported zero skeletons for ${npcData.characterModelId}`,
      );
    }

    return {
      root: instance.root,
      meshes: instance.meshes,
      skeletons: instance.skeletons,
      animationGroups: instance.animationGroups,
      transformNodes: instance.transformNodes,
    };
  }

  /**
   * Loads a character model, its skeleton type's animations and the models
   * of the visual items (gear, outfit) it wears, so a character spawned
   * later loads nothing. Items it cannot wear are skipped, as when equipping.
   */
  async preloadCharacter(
    scene: Scene,
    characterModelId: string,
    visualItemIds: readonly string[],
    caches: { readonly equipmentCache: AssetCache; readonly bodyPartCache: AssetCache },
  ): Promise<void> {
    const skeletonType = this.findSkeletonType(characterModelId);
    if (!skeletonType) {
      throw new Error(`Character model not found: ${characterModelId}`);
    }
    const attachments = resolveEquipmentAttachments(
      skeletonType,
      (itemDefinitionId) => this.findEquipmentPiece(itemDefinitionId),
      (itemDefinitionId) => this.findBodyPart(itemDefinitionId),
      visualItemIds,
      this.getOutfitBody(characterModelId),
    );
    await Promise.all([
      this.cache.preloadCharacter(scene, characterModelId),
      this.loadAnimationBase(scene, characterModelId),
      this.loadAnimationVocabulary(characterModelId),
      ...attachments.map((attachment) =>
        (attachment.catalogType === 'body-part' ? caches.bodyPartCache : caches.equipmentCache)
          .preload(scene, attachment.model, attachment.itemDefinitionId)),
    ]);
  }

  /**
   * Get the character model data (includes scale factor if set).
   */
  findCharacterModel(characterModelId: string): CharacterModel | undefined {
    return this.characters.get(characterModelId);
  }

  /** The body outfit armor dresses for this character model (see `expandOutfitItemIds`). */
  getOutfitBody(characterModelId: string): OutfitBody {
    return { characterModelId, defaultOutfitItemId: this.findCharacterModel(characterModelId)?.defaultOutfitItemId };
  }

  /** Every character model in the catalog (e.g. to populate a character-selection list). */
  getAllCharacterModels(): CharacterModel[] {
    return Array.from(this.characters.values());
  }

  /**
   * Get a single body part by id (cosmetic/outfit piece).
   */
  findBodyPart(bodyPartId: string): BodyPart | undefined {
    return this.bodyParts.get(bodyPartId);
  }

  /** Every body part in the catalog (cosmetic/outfit pieces). */
  getAllBodyParts(): BodyPart[] {
    return Array.from(this.bodyParts.values());
  }

  /**
   * Get a visual attachment (body part or equipment piece) by id.
   * Checks body parts first, then falls back to equipment.
   */
  findVisualAttachment(id: string): VisualAttachmentSource | undefined {
    return this.bodyParts.get(id) ?? this.equipment.get(id);
  }

  /**
   * Get the animation vocabulary for a character's skeleton type.
   */
  async loadAnimationVocabulary(characterModelId: string): Promise<AnimationVocabulary> {
    const charModel = this.characters.get(characterModelId);
    if (!charModel) {
      throw new Error(`Character model not found: ${characterModelId}`);
    }

    const skelType = this.skeletonTypes.find(st => st.id === charModel.skeletonTypeId);
    if (!skelType) {
      throw new Error(`Skeleton type not found: ${charModel.skeletonTypeId}`);
    }

    return this.cache.loadAnimationVocabulary(
      skelType.id,
      `${this.apiBaseUrl}/skeleton-types/${skelType.id}/animations`,
    );
  }

  /**
   * Get the shared animation base (skeleton + baked animation groups) for a
   * character's skeleton type - parsed at most once per skeleton type per
   * `CharacterLoader` instance, regardless of how many entities share it.
   * Callers must clone-and-retarget the result (`cloneRetargetedAnimationGroups`)
   * rather than mutating it, since it's reused for every subsequent caller.
   */
  async loadAnimationBase(scene: Scene, characterModelId: string): Promise<AnimationBase> {
    const skeletonTypeId = this.getSkeletonTypeId(characterModelId);
    return this.cache.loadAnimationBase(skeletonTypeId, async () => {
      const animData = await loadAnimationBaseBytes(skeletonTypeId);
      const animDataUri = arrayBufferToDataUri(animData);
      return loadAnimationBase(scene, '', animDataUri);
    });
  }

  /**
   * Resolve a character model's skeleton type id - the actual identity of
   * the shared animation-base resource (many character models share one
   * skeleton type, and therefore one animation-base file). Callers that
   * cache/dedupe that fetch should key on this, not on characterModelId -
   * keying per-character caused every character model sharing "humanoid"
   * to fetch and cache its own separate copy of the same file, including
   * firing concurrent duplicate network requests for it when several such
   * characters loaded at once.
   */
  getSkeletonTypeId(characterModelId: string): string {
    const charModel = this.characters.get(characterModelId);
    if (!charModel) {
      throw new Error(`Character model not found: ${characterModelId}`);
    }
    return charModel.skeletonTypeId;
  }

  /** Resolve a character model's full `SkeletonType` (equipment slots, bone names) - e.g. for equipment-mesh attachment. */
  findSkeletonType(characterModelId: string): SkeletonType | undefined {
    const charModel = this.characters.get(characterModelId);
    if (!charModel) {
      return undefined;
    }
    return this.skeletonTypes.find((st) => st.id === charModel.skeletonTypeId);
  }

  /** Resolve an equipped item's catalog definition (slot, model path) by its item definition id. */
  findEquipmentPiece(itemDefinitionId: string): EquipmentPiece | undefined {
    return this.equipment.get(itemDefinitionId);
  }

  /**
   * Puts `piece` in the catalog in place of the entry with its id (or adds
   * it), for this loader only: the item editor's preview tries an unsaved
   * entry this way. Characters pick it up on their next equipment update.
   */
  setEquipmentPiece(piece: EquipmentPiece): void {
    this.equipment.set(piece.id, piece);
  }

  /** Every equipment piece in the catalog (e.g. to populate a per-slot selection UI). */
  getAllEquipmentPieces(): EquipmentPiece[] {
    return Array.from(this.equipment.values());
  }

  /**
   * Dresses a loaded character in one of its model's texture variants (an
   * elite's coloring). Each body material is cloned once per scene, model and
   * variant, with the variant's textures in place of the model's own, and
   * every character wearing that variant shares the clone - instances share
   * their model's materials, so the swap cannot be made in place.
   *
   * An unknown variant leaves the character as it is.
   */
  async applyTextureVariant(
    scene: Scene,
    characterModelId: string,
    variantId: string,
    meshes: readonly AbstractMesh[],
  ): Promise<void> {
    const variant = this.characters.get(characterModelId)?.textureVariants?.[variantId];
    if (!variant) {
      return;
    }
    await Promise.all(meshes.map(async (mesh) => {
      const source = mesh.material;
      if (!(source instanceof PBRMaterial)) {
        return;
      }
      const key = `${scene.uniqueId}:${characterModelId}:${variantId}:${source.uniqueId}`;
      mesh.material = await this.variantMaterials.get(key, () => createVariantMaterial(scene, source, variant, `${source.name}:${variantId}`));
    }));
  }

  dispose(): void {
    this.variantMaterials.clear();
    this.cache.dispose();
    this.characters.clear();
    this.equipment.clear();
  }
}

/** A clone of `source` wearing `variant`'s textures, once they have loaded. */
async function createVariantMaterial(
  scene: Scene,
  source: PBRMaterial,
  variant: CharacterTextureVariant,
  name: string,
): Promise<PBRMaterial> {
  const [baseColor, orm, emissive] = await Promise.all(
    [variant.baseColor, variant.orm, variant.emissive].map((path) => (path ? loadVariantTexture(scene, path) : undefined)),
  );
  const material = source.clone(name);
  if (baseColor) {
    material.albedoTexture = baseColor;
  }
  if (orm) {
    // The glTF loader set this texture's channel flags (roughness from
    // green, metal from blue); the clone keeps them.
    material.metallicTexture = orm;
  }
  if (emissive) {
    // A model with no glow of its own has a black emissive color.
    material.emissiveTexture = emissive;
    material.emissiveColor = Color3.White();
    material.emissiveIntensity = variant.emissiveStrength ?? 1;
  }
  return material;
}

/** Loads one variant texture, glTF-oriented (not flipped). */
async function loadVariantTexture(scene: Scene, path: string): Promise<Texture> {
  const data = await loadCharacterTextureBytes(path);
  return new Promise((resolve, reject) => {
    const texture: Texture = new Texture(arrayBufferToDataUri(data, 'image/webp'), scene, {
      invertY: false,
      mimeType: 'image/webp',
      onLoad: () => resolve(texture),
      onError: (message) => reject(new Error(`Texture variant ${path} failed to load: ${message ?? 'unknown error'}`)),
    });
  });
}
